import moment from 'moment';

import { useGetSpaClasses } from '@features/spa/hooks/useGetSpaClasses';
import { useGetSpaClassesPage } from '@features/spa/hooks/useGetSpaClassesPage';
import { useLoadingState } from '@common/hooks';
import { CategoryFilterValue } from '@features/spa/hooks/useSpaEntityFilter';
import { UNCATEGORIZED_VALUE } from '@features/spa/utils/buildCategoryOptions';
import { mapDiscountToDisplayObject } from '@utils/mapDiscountToDisplayObject';
import { SpaClassListItem } from 'voyager';
import { SpaClass } from '@features/spa/pages/SpaClasses/SpaClasses.types';
import { SpaClassAvialability } from '@features/spa/spa.types';

export type SpaClassCategory = { id: number; name: string };

export type SpaClassTimeslotForList = SpaClassAvialability & {
  classId: number;
  discount?: {
    discountedPrice?: { amount: number; currencyCode: string } | null;
    standardPrice?: { amount: number; currencyCode: string } | null;
    name?: string;
  } | null;
};

export type SpaClassesListResult = {
  listItems: SpaClassListItem[];
  classesById: Record<number, SpaClass>;
  timeslotsByKey: Record<string, SpaClassTimeslotForList>;
  categories: SpaClassCategory[];
  uncategorizedItems: SpaClass[];
  isInitialLoading: boolean;
  isRefetching: boolean;
};

const buildTimeslotKey = (classId: number, startDateTime: string) => `${classId}|${startDateTime}`;

const normaliseInstructor = (staffName: string | null | undefined): string | undefined => {
  if (!staffName) return undefined;
  const trimmed = staffName.trim();
  if (!trimmed) return undefined;
  if (trimmed.replace(/-/g, '').toLowerCase() === 'none') return undefined;
  return trimmed;
};

const hasFutureTimeslot = (spaClass: SpaClass): boolean =>
  spaClass.availability?.some((availability) =>
    moment(availability.startDateTime).isAfter(moment())
  ) ?? false;

type UseSpaClassesListParams = {
  propertyId: number;
  language: string;
  date: string;
  categoryFilterId?: number | null;
  categoryFilterValue?: CategoryFilterValue;
};

export function useSpaClassesList({
  propertyId,
  language,
  date,
  categoryFilterId,
  categoryFilterValue,
}: UseSpaClassesListParams): SpaClassesListResult {
  const { data: classes = [], isFetching } = useGetSpaClasses(propertyId, language, date);
  const { data: classesPage } = useGetSpaClassesPage(propertyId, language);
  const fallbackImageUrl = classesPage?.heroBackgroundImage?.url;

  const hasLoadedState = useLoadingState(isFetching, classes.length > 0);

  const filteredClasses =
    categoryFilterValue === UNCATEGORIZED_VALUE
      ? classes.filter((c) => !c.categories || c.categories.length === 0)
      : categoryFilterId
      ? classes.filter((c) =>
          c.categories?.find(
            (cat: { id: number | string }) => String(cat.id) === String(categoryFilterId)
          )
        )
      : classes;

  const bookableClasses = classes.filter(hasFutureTimeslot);

  const categoriesById = new Map<string, SpaClassCategory>();
  bookableClasses.forEach((c) =>
    c.categories?.forEach((cat) => {
      if (cat?.id != null) categoriesById.set(String(cat.id), { id: cat.id, name: cat.name });
    })
  );
  const categories = Array.from(categoriesById.values());
  const uncategorizedItems = bookableClasses.filter(
    (c) => !c.categories || c.categories.length === 0
  );

  const classesById: Record<number, SpaClass> = {};
  const timeslotsByKey: Record<string, SpaClassTimeslotForList> = {};
  const listItems: SpaClassListItem[] = [];

  filteredClasses.forEach((spaClass) => {
    classesById[spaClass.id] = spaClass;

    spaClass.availability?.forEach((availability) => {
      if (!moment(availability.startDateTime).isAfter(moment())) return;

      const key = buildTimeslotKey(spaClass.id, availability.startDateTime);
      const timeslotWithClass: SpaClassTimeslotForList = {
        ...availability,
        classId: spaClass.id,
        discount: (availability as any).discount ?? (spaClass as any).discount ?? null,
      };
      timeslotsByKey[key] = timeslotWithClass;

      const duration = moment(availability.endDateTime).diff(
        moment(availability.startDateTime),
        'minutes'
      );

      const discountDisplay = mapDiscountToDisplayObject(timeslotWithClass.discount as any);

      listItems.push({
        id: key,
        name: spaClass.name,
        time: moment(availability.startDateTime).format('HH:mm'),
        imageUrl: spaClass.image?.url || fallbackImageUrl,
        hideImage: !spaClass.image?.url && !fallbackImageUrl,
        price: availability.price?.amount != null ? availability.price : undefined,
        discount: discountDisplay,
        promotionName: timeslotWithClass.discount?.name,
        duration: duration > 0 ? `${duration} mins` : undefined,
        instructor: normaliseInstructor(availability.staffName),
        spotsLeft: availability.numberOfSpacesAvailable,
        unavailable: availability.isSuspended || availability.numberOfSpacesAvailable === 0,
      });
    });
  });

  listItems.sort(
    (a, b) =>
      moment(timeslotsByKey[a.id].startDateTime).valueOf() -
      moment(timeslotsByKey[b.id].startDateTime).valueOf()
  );

  return {
    listItems,
    classesById,
    timeslotsByKey,
    categories,
    uncategorizedItems,
    ...hasLoadedState,
  };
}

export { buildTimeslotKey };
